import { hashWords } from "daedalus-core";
import { ENABLED_SETS, isBarePiece, type LookPiece, type LookSlot } from "../rules/look-records.ts";
import type { Looks } from "../rules/looks.ts";
import type { Appearance } from "./look.ts";

/**
 * The character creator's state, as pure rules (ADR-094 decisions 1 and 24 to 26): what the Look step offers, built
 * from the looks table and the enabled combat sets; the rows a player changes; the reducer that applies a change;
 * Randomise from a seed; and the `look` field the creation request carries. The state is an `Appearance`, so every
 * state the reducer reaches is one `validateAppearance` accepts under the same table and sets.
 */

/**
 * One choosable piece in a row: its key, its colour number and its label.
 */
export interface CreatorSwatch {
  readonly key: string;
  readonly colour: number;
  readonly label: string;
}

/**
 * A style a player may choose, with the colours it is offered in, in the table's order. A bare style is "None": it
 * holds the one piece that draws nothing, and its colour row is not shown.
 */
export interface CreatorStyle {
  readonly style: string;
  readonly label: string;
  readonly bare: boolean;
  readonly colours: readonly CreatorSwatch[];
}

/**
 * What the Look step offers: every skin as a swatch, and hair and clothes grouped by style. Only pieces a player may
 * choose that cover every enabled set are in it (decision 18); hats and the hands never are (decision 26).
 */
export interface CreatorOffer {
  readonly skins: readonly CreatorSwatch[];
  readonly hair: readonly CreatorStyle[];
  readonly clothes: readonly CreatorStyle[];
}

/**
 * The creator's rows, in the order the Look step shows them (decision 24): skin; hair style and its colour; clothes
 * style and its colour. A style row and its colour row together choose one piece.
 */
export const CREATOR_ROWS = ["skin", "hairStyle", "hairColour", "clothesStyle", "clothesColour"] as const;
export type CreatorRow = (typeof CREATOR_ROWS)[number];

/**
 * A change a page asks of the creator: a step along a row (left or right, wrapping), a choice of a row's option by its
 * place, or Randomise from a seed.
 */
export type CreatorAction = { readonly kind: "step"; readonly row: CreatorRow; readonly by: number } | { readonly kind: "choose"; readonly row: CreatorRow; readonly index: number } | { readonly kind: "randomise"; readonly seed: number };

/**
 * One option of a row as a page draws it: the key it stands for (a piece key, or a style for a style row) and its
 * label.
 */
export interface CreatorOption {
  readonly key: string;
  readonly label: string;
}

/**
 * A row as a page draws it: its options and the place of the chosen one. A hidden row is not drawn and takes no
 * focus: the colour row of a style that is "None", which has no option and a place of -1.
 */
export interface CreatorRowView {
  readonly options: readonly CreatorOption[];
  readonly index: number;
  readonly hidden: boolean;
}

/**
 * The seed of the hash Randomise uses, distinct from the default look's.
 */
const RANDOMISE_SEED = 0x72616e64;
const MAX_U32 = 0xffffffff;

/**
 * A style's label: its pieces' label without the colour number they end in, or the style itself when the label does
 * not end in it. A bare piece's label is its style's, whole: "None".
 *
 * @param piece The style's first offered piece.
 * @returns The label.
 */
function styleLabel(piece: LookPiece): string {
  if (isBarePiece(piece)) {
    return piece.label;
  }
  const suffix = ` ${piece.colour}`;
  return piece.label.endsWith(suffix) && piece.label.length > suffix.length ? piece.label.slice(0, -suffix.length) : piece.style;
}

/**
 * A piece as a swatch.
 *
 * @param piece The piece.
 * @returns The swatch.
 */
function swatchOf(piece: LookPiece): CreatorSwatch {
  return Object.freeze({ key: piece.key, colour: piece.colour, label: piece.label });
}

/**
 * The pieces of a slot a player may choose that cover every one of some sets, in the table's order.
 *
 * @param looks The looks.
 * @param slot The slot.
 * @param enabledSets The sets.
 * @param field The appearance field the slot fills, for the error.
 * @returns The pieces.
 * @throws Error when there is none.
 */
function offeredPieces(looks: Looks, slot: LookSlot, enabledSets: readonly string[], field: string): LookPiece[] {
  const offered = looks.pieces.filter((piece) => {
    const held: readonly string[] = piece.sets;
    return piece.slot === slot && piece.creator && enabledSets.every((set) => held.includes(set));
  });
  if (offered.length === 0) {
    throw new Error(`the looks table offers no ${field} piece under the sets ${enabledSets.join(", ")}, so the creator has nothing to show`);
  }
  return offered;
}

/**
 * Groups pieces by style, in the order each style first appears.
 *
 * @param pieces The pieces.
 * @returns The styles.
 */
function stylesOf(pieces: readonly LookPiece[]): readonly CreatorStyle[] {
  const order: string[] = [];
  const groups = new Map<string, LookPiece[]>();
  for (const piece of pieces) {
    const group = groups.get(piece.style);
    if (group === undefined) {
      order.push(piece.style);
      groups.set(piece.style, [piece]);
    } else {
      group.push(piece);
    }
  }
  return Object.freeze(
    order.map((style) => {
      const group = groups.get(style) as LookPiece[];
      return Object.freeze({ style, label: styleLabel(group[0] as LookPiece), bare: isBarePiece(group[0] as LookPiece), colours: Object.freeze(group.map(swatchOf)) });
    }),
  );
}

/**
 * What the Look step offers (ADR-094 decision 24): every skin a player may choose, and its hair and clothes grouped by
 * style, each style with the colours it is offered in, all in the table's order. A piece is offered when it is
 * `creator` and covers every enabled set, which is exactly what `validateAppearance` accepts in its field. The hair
 * that is none is a style of its own, marked `bare` and labelled "None".
 *
 * @param looks The looks.
 * @param enabledSets The combat sets whose weapons the game ships; `ENABLED_SETS` unless given.
 * @returns The offer, frozen.
 * @throws Error when a field has nothing to offer under those sets, which no validated table does under the game's.
 * @example
 * creatorOffer(loadRules().looks).hair.map((style) => style.style);
 * // ["bob1", "dap1"]
 */
export function creatorOffer(looks: Looks, enabledSets: readonly string[] = ENABLED_SETS): CreatorOffer {
  const skins = offeredPieces(looks, "skin", enabledSets, "skin");
  const hair = offeredPieces(looks, "hair", enabledSets, "hair");
  const clothes = offeredPieces(looks, "outfit", enabledSets, "clothes");
  return Object.freeze({ skins: Object.freeze(skins.map(swatchOf)), hair: stylesOf(hair), clothes: stylesOf(clothes) });
}

/**
 * Where a key sits among styles: the style's place and the colour's place in it.
 *
 * @param styles The styles.
 * @param key The piece key.
 * @param field The field, for the error.
 * @returns The two places.
 * @throws Error when no style offers the key.
 */
function placeOf(styles: readonly CreatorStyle[], key: string, field: string): { readonly style: number; readonly colour: number } {
  for (let style = 0; style < styles.length; style += 1) {
    const colour = (styles[style] as CreatorStyle).colours.findIndex((swatch) => swatch.key === key);
    if (colour >= 0) {
      return { style, colour };
    }
  }
  throw new Error(`the creator's ${field} ${key} is not in the offer`);
}

/**
 * Where a skin sits among the offered skins.
 *
 * @param offer The offer.
 * @param key The skin's key.
 * @returns Its place.
 * @throws Error when the offer lacks it.
 */
function skinPlace(offer: CreatorOffer, key: string): number {
  const index = offer.skins.findIndex((swatch) => swatch.key === key);
  if (index < 0) {
    throw new Error(`the creator's skin ${key} is not in the offer`);
  }
  return index;
}

/**
 * A row as a page draws it: its options and the place of the one the state has chosen. A colour row lists the colours
 * of the chosen style alone, and is hidden with no option when that style is "None".
 *
 * @param offer The offer.
 * @param state The creator's state.
 * @param row The row.
 * @returns The options and the chosen place.
 * @throws Error when the state names a piece the offer lacks, which no state the creator reaches does.
 */
export function creatorRow(offer: CreatorOffer, state: Appearance, row: CreatorRow): CreatorRowView {
  switch (row) {
    case "skin":
      return { options: offer.skins.map((swatch) => ({ key: swatch.key, label: swatch.label })), index: skinPlace(offer, state.skin), hidden: false };
    case "hairStyle":
    case "clothesStyle": {
      const styles = row === "hairStyle" ? offer.hair : offer.clothes;
      const place = placeOf(styles, row === "hairStyle" ? state.hair : state.clothes, row === "hairStyle" ? "hair" : "clothes");
      return { options: styles.map((style) => ({ key: style.style, label: style.label })), index: place.style, hidden: false };
    }
    case "hairColour":
    case "clothesColour": {
      const styles = row === "hairColour" ? offer.hair : offer.clothes;
      const place = placeOf(styles, row === "hairColour" ? state.hair : state.clothes, row === "hairColour" ? "hair" : "clothes");
      const chosen = styles[place.style] as CreatorStyle;
      if (chosen.bare) {
        return { options: [], index: -1, hidden: true };
      }
      return { options: chosen.colours.map((swatch) => ({ key: swatch.key, label: swatch.label })), index: place.colour, hidden: false };
    }
  }
}

/**
 * A style's piece in a colour: the same colour number when the style has it, else its first.
 *
 * @param style The style.
 * @param colour The colour number to keep.
 * @returns The piece key.
 */
function keepColour(style: CreatorStyle, colour: number): string {
  return (style.colours.find((swatch) => swatch.colour === colour) ?? (style.colours[0] as CreatorSwatch)).key;
}

/**
 * The key a row's option stands for, with the rest of the state kept: a skin, a colour of the chosen style, or a
 * style in the colour held when it has it.
 *
 * @param offer The offer.
 * @param state The state.
 * @param row The row.
 * @param index The option's place, already in range.
 * @returns The new state.
 */
function withOption(offer: CreatorOffer, state: Appearance, row: CreatorRow, index: number): Appearance {
  switch (row) {
    case "skin":
      return { skin: (offer.skins[index] as CreatorSwatch).key, hair: state.hair, clothes: state.clothes };
    case "hairStyle": {
      const place = placeOf(offer.hair, state.hair, "hair");
      const colour = ((offer.hair[place.style] as CreatorStyle).colours[place.colour] as CreatorSwatch).colour;
      return { skin: state.skin, hair: keepColour(offer.hair[index] as CreatorStyle, colour), clothes: state.clothes };
    }
    case "hairColour": {
      const place = placeOf(offer.hair, state.hair, "hair");
      return { skin: state.skin, hair: ((offer.hair[place.style] as CreatorStyle).colours[index] as CreatorSwatch).key, clothes: state.clothes };
    }
    case "clothesStyle": {
      const place = placeOf(offer.clothes, state.clothes, "clothes");
      const colour = ((offer.clothes[place.style] as CreatorStyle).colours[place.colour] as CreatorSwatch).colour;
      return { skin: state.skin, hair: state.hair, clothes: keepColour(offer.clothes[index] as CreatorStyle, colour) };
    }
    case "clothesColour": {
      const place = placeOf(offer.clothes, state.clothes, "clothes");
      return { skin: state.skin, hair: state.hair, clothes: ((offer.clothes[place.style] as CreatorStyle).colours[index] as CreatorSwatch).key };
    }
  }
}

/**
 * Applies one change to the creator's state (ADR-094 decision 24). A step moves along a row by a whole number of
 * places and wraps at either end; a choice takes a row's option by its place; Randomise is `randomiseLook` of its
 * seed. A row changes its own field alone. Changing a style keeps the colour number when the new style has it and
 * takes the new style's first colour otherwise; a hair style of "None" is the hair that is none, and leaving it takes
 * the new style's first colour. A step along a hidden row changes nothing, and a choice on it is out of range. Pure:
 * the state given is never changed.
 *
 * @param offer The offer.
 * @param state The state, one the creator reached.
 * @param action The change.
 * @returns The new state, a new record.
 * @throws RangeError when a choice is not a whole place in the row, a step is not a whole number, or a seed is not an
 *   unsigned 32-bit whole number.
 * @throws Error when the state names a piece the offer lacks.
 * @example
 * reduceCreator(offer, { skin: "humn-1", hair: "bob1-7", clothes: "fstr-2" }, { kind: "step", row: "hairStyle", by: 1 });
 * // { skin: "humn-1", hair: "dap1-7", clothes: "fstr-2" }
 */
export function reduceCreator(offer: CreatorOffer, state: Appearance, action: CreatorAction): Appearance {
  if (action.kind === "randomise") {
    return randomiseLook(offer, action.seed);
  }
  const view = creatorRow(offer, state, action.row);
  const count = view.options.length;
  if (action.kind === "choose") {
    if (!Number.isInteger(action.index) || action.index < 0 || action.index >= count) {
      throw new RangeError(`choice ${action.index} is outside the ${action.row} row's ${count} options`);
    }
    return withOption(offer, state, action.row, action.index);
  }
  if (!Number.isInteger(action.by)) {
    throw new RangeError(`a step of ${action.by} along the ${action.row} row is not a whole number`);
  }
  if (view.hidden) {
    return { skin: state.skin, hair: state.hair, clothes: state.clothes };
  }
  return withOption(offer, state, action.row, (((view.index + action.by) % count) + count) % count);
}

/**
 * A pick among a count by the Randomise hash of a seed and a field's place.
 *
 * @param words The two-word buffer, the seed in its first word.
 * @param place The field's place.
 * @param count How many to pick among, 1 or more.
 * @returns The place picked.
 */
function pick(words: Uint32Array, place: number, count: number): number {
  words[1] = place;
  return hashWords(RANDOMISE_SEED, words, 2) % count;
}

/**
 * Randomise (ADR-094 decision 24): a look picked from the offer by the shared seeded hash of a seed, so the same seed
 * gives the same look on every engine. A skin is picked among the skins; hair and clothes pick a style, then a colour
 * of it, so each style is as likely as another whatever its count of colours; "None" is a hair style as any other, so
 * Randomise gives no hair as often as it gives any one style. The page draws its seeds; nothing here reads a clock or
 * `Math.random()`.
 *
 * @param offer The offer.
 * @param seed An unsigned 32-bit whole number.
 * @returns The look, a new record.
 * @throws RangeError when the seed is not an unsigned 32-bit whole number.
 * @example
 * randomiseLook(creatorOffer(loadRules().looks), 7);
 * // one skin, one hair and one outfit, the same wherever it is asked
 */
export function randomiseLook(offer: CreatorOffer, seed: number): Appearance {
  if (!Number.isInteger(seed) || seed < 0 || seed > MAX_U32) {
    throw new RangeError(`a randomise seed of ${seed} is not an unsigned 32-bit whole number`);
  }
  const words = new Uint32Array(2);
  words[0] = seed;
  const skin = (offer.skins[pick(words, 0, offer.skins.length)] as CreatorSwatch).key;
  const hairStyle = offer.hair[pick(words, 1, offer.hair.length)] as CreatorStyle;
  const hair = (hairStyle.colours[pick(words, 2, hairStyle.colours.length)] as CreatorSwatch).key;
  const clothesStyle = offer.clothes[pick(words, 3, offer.clothes.length)] as CreatorStyle;
  const clothes = (clothesStyle.colours[pick(words, 4, clothesStyle.colours.length)] as CreatorSwatch).key;
  return { skin, hair, clothes };
}

/**
 * Whether the offer holds a key in a field.
 *
 * @param offer The offer.
 * @param field The field.
 * @param key The key.
 * @returns True when the field's row offers it.
 */
function offers(offer: CreatorOffer, field: "skin" | "hair" | "clothes", key: string): boolean {
  if (field === "skin") {
    return offer.skins.some((swatch) => swatch.key === key);
  }
  return (field === "hair" ? offer.hair : offer.clothes).some((style) => style.colours.some((swatch) => swatch.key === key));
}

/**
 * The offer with its bare styles left out: what a seed picks from when nobody chose.
 *
 * @param offer The offer.
 * @returns The offer, its hair and clothes holding only styles with art.
 */
function withArt(offer: CreatorOffer): CreatorOffer {
  return { skins: offer.skins, hair: offer.hair.filter((style) => !style.bare), clothes: offer.clothes.filter((style) => !style.bare) };
}

/**
 * The state the Look step opens on: for a new character, the look its seed picks; for Change look (decision 29), the
 * character's own look, each field the offer lacks (a withdrawn piece, or a key of a later table) replaced by the
 * seed's pick for that field alone. So the creator always opens on a state it can reach. The seed picks among styles
 * with art alone: the step opens on no hair only for a character that already has none, so nobody confirms a bald
 * character without having chosen it or pressed Randomise.
 *
 * @param offer The offer.
 * @param current The character's look, or null for a new character.
 * @param seed An unsigned 32-bit whole number.
 * @returns The state.
 * @throws RangeError when the seed is not an unsigned 32-bit whole number.
 */
export function creatorStart(offer: CreatorOffer, current: Appearance | null, seed: number): Appearance {
  const picked = randomiseLook(withArt(offer), seed);
  if (current === null) {
    return picked;
  }
  return {
    skin: offers(offer, "skin", current.skin) ? current.skin : picked.skin,
    hair: offers(offer, "hair", current.hair) ? current.hair : picked.hair,
    clothes: offers(offer, "clothes", current.clothes) ? current.clothes : picked.clothes,
  };
}

/**
 * The `look` field of the creation and change-look requests (decision 23): exactly the three keys, in a new record,
 * whatever else the object given carries, so the gateway's `validateAppearance` sees its own shape.
 *
 * @param state The creator's state.
 * @returns The field.
 */
export function creatorLookField(state: Appearance): Appearance {
  return { skin: state.skin, hair: state.hair, clothes: state.clothes };
}
